/**
 * OrganizeFilesModal - Modal for AI-powered file organization workflow.
 *
 * Flow:
 * 1. User clicks "Organize" button
 * 2. Modal opens, starts workflow, shows loading state
 * 3. Once proposal is ready, displays it for review
 * 4. User can modify, approve, or reject the proposal
 * 5. If approved, applies the organization and closes
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  FileMove,
  ProposedOrganization,
  useGetOrganizeFilesStatusQuery,
  useStartOrganizeFilesMutation,
  useSubmitOrganizeFilesApprovalMutation,
} from '@/services/api/agentflowService';
import { useGetCollectionListQuery } from '@/services/api/collectionService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrganizeFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
}

type ModalStep =
  | 'starting'
  | 'analyzing'
  | 'review'
  | 'applying'
  | 'complete'
  | 'error';

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
  </div>
);

const StatusMessage = ({
  status,
  message,
}: {
  status: ModalStep;
  message: string;
}) => {
  const icons: Record<ModalStep, string> = {
    starting: '🚀',
    analyzing: '🔍',
    review: '📋',
    applying: '⚡',
    complete: '✅',
    error: '❌',
  };

  return (
    <div className="flex items-center gap-3 py-4">
      <span className="text-2xl">{icons[status]}</span>
      <span className="text-sm text-zinc-600">{message}</span>
    </div>
  );
};

const FileMoveItem = ({
  move,
  onChange,
  onRemove,
  availableFolders,
}: {
  move: FileMove;
  onChange: (updated: FileMove) => void;
  onRemove: () => void;
  availableFolders: string[];
}) => (
  <div className="flex items-center gap-2 p-2 bg-zinc-50 rounded-md group">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate" title={move.file_name}>
        {move.file_name}
      </div>
      <div className="flex items-center gap-1 text-xs text-zinc-500">
        <span>→</span>
        <select
          value={move.target_collection_name}
          onChange={(e) =>
            onChange({ ...move, target_collection_name: e.target.value })
          }
          className="bg-white border border-zinc-200 rounded px-2 py-0.5 text-xs focus:border-primary focus:outline-none min-w-[140px]"
        >
          {availableFolders.map((folder) => (
            <option key={folder} value={folder}>
              {folder}
            </option>
          ))}
          {/* Allow custom folder if current value not in list */}
          {!availableFolders.includes(move.target_collection_name) && (
            <option value={move.target_collection_name}>
              {move.target_collection_name} (custom)
            </option>
          )}
        </select>
      </div>
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-opacity"
      title="Remove"
    >
      ×
    </button>
  </div>
);

const ProposalEditor = ({
  proposal,
  onUpdate,
  matterId,
}: {
  proposal: ProposedOrganization;
  onUpdate: (updated: ProposedOrganization) => void;
  matterId: string;
}) => {
  // Fetch existing collections for the matter
  const { data: existingCollections } = useGetCollectionListQuery(matterId);

  // Build combined list of available folders: existing + new ones being created
  const availableFolders = useMemo(() => {
    const existingNames = existingCollections?.map((c) => c.name) || [];
    const newNames = proposal.collections_to_create;

    // Combine and deduplicate (case-insensitive)
    const allNames = new Set<string>();
    const result: string[] = [];

    // Add existing folders first
    existingNames.forEach((name) => {
      const lowerName = name.toLowerCase();
      if (!allNames.has(lowerName)) {
        allNames.add(lowerName);
        result.push(name);
      }
    });

    // Add new folders being created
    newNames.forEach((name) => {
      const lowerName = name.toLowerCase();
      if (!allNames.has(lowerName)) {
        allNames.add(lowerName);
        result.push(name);
      }
    });

    return result.sort((a, b) => a.localeCompare(b));
  }, [existingCollections, proposal.collections_to_create]);

  const handleCollectionChange = (index: number, value: string) => {
    const updated = [...proposal.collections_to_create];
    updated[index] = value;
    onUpdate({ ...proposal, collections_to_create: updated });
  };

  const handleRemoveCollection = (index: number) => {
    const updated = proposal.collections_to_create.filter(
      (_, i) => i !== index,
    );
    onUpdate({ ...proposal, collections_to_create: updated });
  };

  const handleAddCollection = () => {
    onUpdate({
      ...proposal,
      collections_to_create: [...proposal.collections_to_create, 'New Folder'],
    });
  };

  const handleFileMoveChange = (index: number, updated: FileMove) => {
    const moves = [...proposal.file_moves];
    moves[index] = updated;
    onUpdate({ ...proposal, file_moves: moves });
  };

  const handleRemoveFileMove = (index: number) => {
    const moves = proposal.file_moves.filter((_, i) => i !== index);
    onUpdate({ ...proposal, file_moves: moves });
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <div className="text-sm text-blue-800">{proposal.summary}</div>
      </div>

      {/* Folders to Create */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-zinc-700">
            Folders to Create
          </h4>
          <button
            type="button"
            onClick={handleAddCollection}
            className="text-xs text-primary hover:text-primary/80"
          >
            + Add folder
          </button>
        </div>
        <div className="space-y-2">
          {proposal.collections_to_create.length === 0 ? (
            <div className="text-sm text-zinc-400 italic">
              No new folders needed
            </div>
          ) : (
            proposal.collections_to_create.map((name, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) =>
                    handleCollectionChange(index, e.target.value)
                  }
                  className="flex-1 px-2 py-1 text-sm border border-zinc-200 rounded focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveCollection(index)}
                  className="text-zinc-400 hover:text-red-500"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* File Moves */}
      <div>
        <h4 className="text-sm font-medium text-zinc-700 mb-2">
          Files to Move ({proposal.file_moves.length})
        </h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {proposal.file_moves.length === 0 ? (
            <div className="text-sm text-zinc-400 italic">No files to move</div>
          ) : (
            proposal.file_moves.map((move, index) => (
              <FileMoveItem
                key={move.file_name}
                move={move}
                availableFolders={availableFolders}
                onChange={(updated) => handleFileMoveChange(index, updated)}
                onRemove={() => handleRemoveFileMove(index)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ResultDisplay = ({
  result,
}: {
  result: {
    success: boolean;
    collections_created: string[];
    files_moved: number;
    files_renamed: number;
    errors: string[];
  };
}) => (
  <div className="space-y-4">
    <div
      className={`p-4 rounded-lg ${result.success ? 'bg-green-50' : 'bg-red-50'}`}
    >
      <div
        className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}
      >
        {result.success
          ? 'Organization Applied Successfully!'
          : 'Organization Completed with Errors'}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 text-center">
      <div className="p-3 bg-zinc-50 rounded-lg">
        <div className="text-2xl font-bold text-primary">
          {result.collections_created.length}
        </div>
        <div className="text-xs text-zinc-500">Folders Created</div>
      </div>
      <div className="p-3 bg-zinc-50 rounded-lg">
        <div className="text-2xl font-bold text-primary">
          {result.files_moved}
        </div>
        <div className="text-xs text-zinc-500">Files Moved</div>
      </div>
      <div className="p-3 bg-zinc-50 rounded-lg">
        <div className="text-2xl font-bold text-primary">
          {result.files_renamed}
        </div>
        <div className="text-xs text-zinc-500">Files Renamed</div>
      </div>
    </div>
    {result.errors.length > 0 && (
      <div className="p-3 bg-red-50 rounded-lg">
        <div className="text-sm font-medium text-red-800 mb-1">Errors:</div>
        <ul className="text-xs text-red-600 list-disc list-inside">
          {result.errors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const OrganizeFilesModal = ({
  isOpen,
  onClose,
  matterId,
}: OrganizeFilesModalProps) => {
  const [step, setStep] = useState<ModalStep>('starting');
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProposedOrganization | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [startWorkflow] = useStartOrganizeFilesMutation();
  const [submitApproval, { isLoading: isSubmitting }] =
    useSubmitOrganizeFilesApprovalMutation();

  // Poll for status when we have a workflow
  const { data: statusData, refetch } = useGetOrganizeFilesStatusQuery(
    { matterId },
    {
      skip: !workflowId || step === 'complete' || step === 'error',
      pollingInterval: step === 'analyzing' || step === 'applying' ? 2000 : 0,
    },
  );

  // Start workflow when modal opens (or resume existing one)
  useEffect(() => {
    if (!isOpen) return;

    const start = async () => {
      setStep('starting');
      setErrorMessage(null);
      setProposal(null);

      try {
        const response = await startWorkflow({
          matterId,
          request: {
            require_approval: true,
            approval_timeout_hours: 1,
          },
        }).unwrap();

        setWorkflowId(response.workflow_id);

        // Check if response includes status info (existing workflow resumed)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const statusResponse = response as any;
        if (statusResponse.status) {
          // Existing workflow - set appropriate step based on status
          if (
            statusResponse.status === 'awaiting_approval' &&
            statusResponse.proposed_organization
          ) {
            setProposal(statusResponse.proposed_organization);
            setStep('review');
          } else if (statusResponse.status === 'applying') {
            setStep('applying');
          } else if (statusResponse.status === 'completed') {
            setStep('complete');
          } else if (statusResponse.status === 'failed') {
            setErrorMessage('Workflow failed');
            setStep('error');
          } else {
            // analyzing, initializing, etc.
            setStep('analyzing');
          }
        } else {
          // New workflow started
          setStep('analyzing');
        }
      } catch (error) {
        setErrorMessage('Failed to start organization workflow');
        setStep('error');
      }
    };

    start();
  }, [isOpen, matterId, startWorkflow]);

  // Update step based on status
  useEffect(() => {
    if (!statusData) return;

    if (
      statusData.status === 'awaiting_approval' &&
      statusData.proposed_organization
    ) {
      setProposal(statusData.proposed_organization);
      setStep('review');
    } else if (statusData.status === 'applying') {
      setStep('applying');
    } else if (statusData.status === 'completed') {
      setStep('complete');
    } else if (statusData.status === 'failed') {
      setErrorMessage('Workflow failed');
      setStep('error');
    } else if (statusData.status === 'analyzing') {
      setStep('analyzing');
    }
  }, [statusData]);

  const handleApprove = useCallback(async () => {
    try {
      await submitApproval({
        matterId,
        request: {
          action: 'approve',
          modified_plan: proposal,
        },
      }).unwrap();
      setStep('applying');
    } catch (error) {
      setErrorMessage('Failed to submit approval');
    }
  }, [matterId, proposal, submitApproval]);

  const handleReject = useCallback(async () => {
    try {
      await submitApproval({
        matterId,
        request: {
          action: 'reject',
          reason: 'User rejected the proposal',
        },
      }).unwrap();
      onClose();
    } catch (error) {
      setErrorMessage('Failed to submit rejection');
    }
  }, [matterId, submitApproval, onClose]);

  const handleClose = () => {
    setWorkflowId(null);
    setProposal(null);
    setStep('starting');
    setErrorMessage(null);
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'starting':
        return 'Starting Organization...';
      case 'analyzing':
        return 'Analyzing Files...';
      case 'review':
        return 'Review Organization Proposal';
      case 'applying':
        return 'Applying Organization...';
      case 'complete':
        return 'Organization Complete';
      case 'error':
        return 'Error';
      default:
        return 'Organize Files';
    }
  };

  const getDescription = () => {
    switch (step) {
      case 'starting':
        return 'Initializing the AI-powered file organization workflow...';
      case 'analyzing':
        return 'AI is analyzing your files and creating an organization plan...';
      case 'review':
        return 'Review and modify the proposed organization before applying.';
      case 'applying':
        return 'Creating folders and moving files...';
      case 'complete':
        return 'Your files have been organized successfully.';
      case 'error':
        return errorMessage || 'An error occurred.';
      default:
        return '';
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg">
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogDescription>{getDescription()}</DialogDescription>

      <DialogBody>
        {/* Loading states */}
        {(step === 'starting' ||
          step === 'analyzing' ||
          step === 'applying') && (
          <>
            <LoadingSpinner />
            <StatusMessage
              status={step}
              message={
                step === 'starting'
                  ? 'Starting workflow...'
                  : step === 'analyzing'
                    ? 'AI is analyzing your files...'
                    : 'Applying changes...'
              }
            />
          </>
        )}

        {/* Review state */}
        {step === 'review' && proposal && (
          <ProposalEditor
            proposal={proposal}
            onUpdate={setProposal}
            matterId={matterId}
          />
        )}

        {/* Complete state */}
        {step === 'complete' && statusData?.apply_result && (
          <ResultDisplay result={statusData.apply_result} />
        )}

        {/* Error state */}
        {step === 'error' && (
          <div className="p-4 bg-red-50 rounded-lg text-red-800">
            {errorMessage || 'An unexpected error occurred.'}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        {step === 'review' && (
          <>
            <Button
              color="white"
              onClick={handleReject}
              disabled={isSubmitting}
            >
              Reject
            </Button>
            <Button
              color="dark/zinc"
              onClick={handleApprove}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Applying...' : 'Approve & Apply'}
            </Button>
          </>
        )}

        {(step === 'complete' || step === 'error') && (
          <Button color="dark/zinc" onClick={handleClose}>
            Close
          </Button>
        )}

        {(step === 'starting' ||
          step === 'analyzing' ||
          step === 'applying') && (
          <Button color="white" onClick={handleClose}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizeFilesModal;
